import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Container, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './fontawesome';

import Header from './components/Layout/Header';
import StepIndicator from './components/common/StepIndicator';
import PersonalProfile from './components/Profile/PersonalProfile';
import FinancialSituation from './components/Profile/FinancialSituation';
import ContributionDetails from './components/Profile/ContributionDetails';
import GoalsObjectives from './components/Profile/GoalsObjectives';
import TaxPosition from './components/Profile/TaxPosition';
import RiskQuestionnaire from './components/Assessment/RiskQuestionnaire';
import CapacityForLoss from './components/Assessment/CapacityForLoss';
import RecommendationCard from './components/Recommendation/RecommendationCard';
import ContributionRecommendation from './components/Recommendation/ContributionRecommendation';
import PensionProjection from './components/Recommendation/PensionProjection';
import SuitabilityReport from './components/Recommendation/SuitabilityReport';
import ComplianceDashboard from './components/Compliance/ComplianceDashboard';
import WaiveAdmin from './components/Admin/WaiveAdmin';
import MethodologyPage from './components/Methodology/MethodologyPage';
import LoginScreen from './components/LoginScreen';
import authService from './services/authService';

// Import from configService
import { fetchQuestions } from './services/configService';

// Import backend API service
import { adviceService } from './services/adviceService';

// Steps definition
const steps = [
    { id: 1, label: 'Personal', icon: 'user' },
    { id: 2, label: 'Financial', icon: 'wallet' },
    { id: 3, label: 'Contributions', icon: 'hand-holding-usd' },
    { id: 4, label: 'Goals', icon: 'bullseye' },
    { id: 5, label: 'Tax Position', icon: 'coins' },
    { id: 6, label: 'Risk Profile', icon: 'balance-scale' },
    { id: 7, label: 'Recommendation', icon: 'check-circle' }
];

const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 'unknown';

// =============================================================================
// Assessment Wizard
// =============================================================================

function AssessmentWizard({ user }) {
    const [currentStep, setCurrentStep] = useState(1);

    // Loading and error states for backend calls
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Questions loaded from API via configService
    const [riskQuestions, setRiskQuestions] = useState([]);
    const [capacityQuestions, setCapacityQuestions] = useState([]);
    const [questionsLoading, setQuestionsLoading] = useState(true);
    const [questionsError, setQuestionsError] = useState(null);

    // Load questions from API on mount using configService
    useEffect(() => {
        async function loadQuestions() {
            try {
                const data = await fetchQuestions();

                // Transform API format to component format
                if (data.tolerance?.questions) {
                    const transformed = data.tolerance.questions.map(q => ({
                        id: q.id,
                        text: q.text,
                        helpText: q.help_text,
                        options: q.options.map(opt => ({
                            text: opt.label,
                            description: opt.description,
                            score: opt.value
                        }))
                    }));
                    setRiskQuestions(transformed);
                }

                if (data.capacity?.questions) {
                    const transformed = data.capacity.questions.map(q => ({
                        id: q.id,
                        text: q.text,
                        helpText: q.help_text,
                        options: q.options.map(opt => ({
                            text: opt.label,
                            description: opt.description,
                            score: opt.value
                        }))
                    }));
                    setCapacityQuestions(transformed);
                }
            } catch (err) {
                console.error('Failed to load questions from API:', err);
                setQuestionsError(err.message || 'Failed to load questions');
            } finally {
                setQuestionsLoading(false);
            }
        }
        loadQuestions();
    }, []);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') return;

        const checkForUpdates = async () => {
            try {
                const response = await fetch('/version.json', { cache: 'no-store' });
                const { version } = await response.json();

                console.log('BUILD_VERSION:', BUILD_VERSION);
                console.log('version.json:', version);
                console.log('Match:', version === BUILD_VERSION);

                if (version !== BUILD_VERSION) {
                    const hasReloaded = sessionStorage.getItem('hasReloaded');
                    console.log('hasReloaded flag:', hasReloaded);
                    if (!hasReloaded) {
                        sessionStorage.setItem('hasReloaded', 'true');
                        window.location.reload();
                    }
                } else {
                    sessionStorage.removeItem('hasReloaded');
                }
            } catch (error) {
                console.log('Version check failed:', error);
            }
        };

        checkForUpdates();
        const interval = setInterval(checkForUpdates, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    // Step 1: Personal Profile
    const [personalProfile, setPersonalProfile] = useState({
        age: 41,
        maritalStatus: 'married',
        dependents: 2,
        employmentStatus: 'employed',
        ukResident: true,
        expectedLifeChanges: []
    });

    // Step 2: Financial Situation
    const [financialProfile, setFinancialProfile] = useState({
        pensionValue: 85000,
        annualSalary: 65000,
        emergencyFund: 'yes_6_months',
        hasDebts: false,
        debtLevel: 'none',
        netMonthlyIncome: 0,
        percentageOfWealth: '25_to_50'
    });

    // Step 3: Contribution Details
    const [contributionDetails, setContributionDetails] = useState({
        employeeContribution: 5,
        employerContribution: 3,
        employerMatchUpTo: 8,
        employerMatchTable: {},
        salarySacrificeAvailable: 'yes',
        monthlyDisposable: 500,
        otherPensions: 'small',
        monthlyEssentials: 2500,
        monthlyNonEssentials: 800,
        savingRegularly: 'yes_small'
    });

    // Step 4: Goals
    const [goals, setGoals] = useState({
        targetRetirementAge: 65,
        yearsToRetirement: 24,
        primaryGoal: 'balanced_growth',
        incomeInRetirement: 35000,
        goalPriority: 'important'
    });

    // Step 5: Tax Position
    const [taxPosition, setTaxPosition] = useState({
        current_isa_contributions: null,
        isa_fully_used: false,
        current_pension_contributions: 0,
        employer_pension_contributions: 0,
        current_monthly_pension_contribution: 0,
        other_income: null,
        has_triggered_taper: false,
        is_potential_high_earner: false,
        has_carry_forward: false,
        carry_forward_years: [
            { tax_year: '2023/24', annual_allowance: 60000, contributions_made: 0 },
            { tax_year: '2022/23', annual_allowance: 40000, contributions_made: 0 },
            { tax_year: '2021/22', annual_allowance: 40000, contributions_made: 0 },
        ],
        target_retirement_income: null,
        state_pension_included: true,
        total_debt_excluding_mortgage: null,
        comfortable_with_debt: true
    });

    // Step 6: Risk Assessment
    const [riskAnswers, setRiskAnswers] = useState({});
    const [capacityAnswers, setCapacityAnswers] = useState({});

    // Step 7: Recommendations
    const [backendRecommendation, setBackendRecommendation] = useState(null);
    const [recommendation, setRecommendation] = useState(null);
    const [contributionRecommendation, setContributionRecommendation] = useState(null);
    const [complianceReport, setComplianceReport] = useState(null);
    const [showSuitabilityReport, setShowSuitabilityReport] = useState(false);

    const handleRiskAnswer = (questionId, optionIndex) => {
        setRiskAnswers({ ...riskAnswers, [questionId]: optionIndex });
    };

    const handleCapacityAnswer = (questionId, optionIndex) => {
        setCapacityAnswers({ ...capacityAnswers, [questionId]: optionIndex });
    };

    const handleTaxPositionChange = (newTaxPosition) => {
        const totalIncome = (financialProfile.annualSalary || 0) + (newTaxPosition.other_income || 0);
        const isHighEarner = totalIncome >= 200000;
        const isaFullyUsed = newTaxPosition.current_isa_contributions >= 20000;

        setTaxPosition({
            ...newTaxPosition,
            is_potential_high_earner: isHighEarner,
            isa_fully_used: isaFullyUsed
        });
    };

    const handleNext = async () => {
        if (currentStep === 6) {
            setLoading(true);
            setError(null);

            try {
                // Build client profile for backend API
                const toleranceScores = riskQuestions.map(q => {
                    const answerIdx = riskAnswers[q.id];
                    return answerIdx !== undefined ? q.options[answerIdx].score : 1;
                });
                const capacityScores = capacityQuestions.map(q => {
                    const answerIdx = capacityAnswers[q.id];
                    return answerIdx !== undefined ? q.options[answerIdx].score : 1;
                });

                const clientProfile = {
                    age: personalProfile.age,
                    annual_income: financialProfile.annualSalary,
                    net_monthly_income: financialProfile.netMonthlyIncome > 0 ? financialProfile.netMonthlyIncome : financialProfile.annualSalary / 12 * 0.75,
                    monthly_expenses: (contributionDetails.monthlyEssentials || 0) + (contributionDetails.monthlyNonEssentials || 0),
                    risk_answers: toleranceScores,
                    capacity_answers: capacityScores,
                    risk_responses: [...toleranceScores, ...capacityScores],
                    existing_pension_value: financialProfile.pensionValue,
                    other_assets: 0,
                    dependents: personalProfile.dependents,
                    years_to_retirement: goals.yearsToRetirement,
                    employer_match_available: contributionDetails.employerContribution > 0,
                    employer_match_rate: Math.min((parseFloat(contributionDetails.employerContribution) || 0) / 100, 1.0),
                    investment_experience: financialProfile.percentageOfWealth,
                    expected_life_changes: personalProfile.expectedLifeChanges || [],
                    monthly_essential_expenses: contributionDetails.monthlyEssentials || 0,
                    monthly_non_essential_expenses: contributionDetails.monthlyNonEssentials || 0,
                    saving_regularly: contributionDetails.savingRegularly || 'no',
                    wrapper_usage: {
                        current_pension_contributions: (parseFloat(contributionDetails.employeeContribution) || 0) / 100 * financialProfile.annualSalary,
                        employer_pension_contributions: (parseFloat(contributionDetails.employerContribution) || 0) / 100 * financialProfile.annualSalary,
                        current_isa_contributions: taxPosition.current_isa_contributions || 0,
                        carry_forward_years: taxPosition.has_carry_forward
                            ? taxPosition.carry_forward_years
                            : null
                    },
                    other_income: taxPosition.other_income || 0,
                    tapered_allowance_info: taxPosition.is_potential_high_earner ? {
                        potentially_affected: true,
                        taper_applies: taxPosition.has_triggered_taper
                    } : null,
                    retirement_target: taxPosition.target_retirement_income ? {
                        target_annual_income: taxPosition.target_retirement_income,
                        state_pension_included: taxPosition.state_pension_included
                    } : (goals.incomeInRetirement ? {
                        target_annual_income: goals.incomeInRetirement,
                        state_pension_included: true
                    } : null),
                    current_monthly_pension_contribution: (parseFloat(contributionDetails.employeeContribution) || 0) / 100 * financialProfile.annualSalary / 12,
                    current_monthly_isa_contribution: 0,
                    total_debt_excluding_mortgage: taxPosition.total_debt_excluding_mortgage || 0,
                    comfortable_with_debt: taxPosition.comfortable_with_debt
                };

                console.log('Sending to backend:', clientProfile);

                const backendResult = await adviceService.generateCompleteAdvice(clientProfile);

                console.log('Backend result:', backendResult);

                setBackendRecommendation(backendResult);

                const fundRecommendations = backendResult.fund_recommendations || [];

                setRecommendation({
                    funds: fundRecommendations.map(fund => ({
                        name: fund.fund.name,
                        provider: 'Legal & General (LGIM)',
                        risk: fund.fund.srri,
                        allocation: fund.allocation_percentage,
                        charge: fund.fund.ter,
                        returns: {
                            oneYear: fund.fund.performance_1yr || 0,
                            threeYear: fund.fund.performance_3yr || 0,
                            fiveYear: fund.fund.performance_5yr || 0
                        }
                    })),
                    allocation: {
                        equity: fundRecommendations.reduce((total, fund) => {
                            const assetClass = fund.fund.asset_class ? fund.fund.asset_class.toLowerCase() : '';
                            return assetClass === 'equity' ? total + (fund.allocation_percentage || 0) : total;
                        }, 0),
                        bond: fundRecommendations.reduce((total, fund) => {
                            const assetClass = fund.fund.asset_class ? fund.fund.asset_class.toLowerCase() : '';
                            return assetClass === 'bond' ? total + (fund.allocation_percentage || 0) : total;
                        }, 0),
                        cash: fundRecommendations.reduce((total, fund) => {
                            const assetClass = fund.fund.asset_class ? fund.fund.asset_class.toLowerCase() : '';
                            return assetClass === 'cash' ? total + (fund.allocation_percentage || 0) : total;
                        }, 0)
                    },
                    riskProfile: {
                        tolerance: backendResult.risk_assessment?.risk_tolerance_score || 3,
                        capacity: backendResult.risk_assessment?.risk_capacity_score || 3,
                        overall: backendResult.risk_assessment?.max_srri || 3
                    },
                    suitabilityScore: backendResult.compliance_report?.suitability_score || 0.85,
                    provider: 'Legal & General (LGIM)',
                    portfolioName: `Risk Level ${backendResult.risk_assessment?.max_srri || 3}`,
                    aiExplanation: backendResult.ai_explanation?.client_friendly || null,
                    complianceReport: backendResult.compliance_report || null
                });

                setContributionRecommendation(backendResult.contribution_recommendation);
                setComplianceReport(backendResult.compliance_report);

            } catch (err) {
                console.error('Recommendation generation failed:', err);
                setError(err.message || 'Failed to generate recommendations. Please try again.');
            } finally {
                setLoading(false);
            }
        }

        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
    };

    const handleBack = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleReset = () => {
        setCurrentStep(1);
        setRiskAnswers({});
        setCapacityAnswers({});
        setRecommendation(null);
        setBackendRecommendation(null);
        setContributionRecommendation(null);
        setComplianceReport(null);
        setTaxPosition({
            current_isa_contributions: null,
            isa_fully_used: false,
            current_pension_contributions: 0,
            employer_pension_contributions: 0,
            current_monthly_pension_contribution: 0,
            other_income: null,
            has_triggered_taper: false,
            is_potential_high_earner: false,
            has_carry_forward: false,
            carry_forward_years: [
                { tax_year: '2023/24', annual_allowance: 60000, contributions_made: 0 },
                { tax_year: '2022/23', annual_allowance: 40000, contributions_made: 0 },
                { tax_year: '2021/22', annual_allowance: 40000, contributions_made: 0 },
            ],
            target_retirement_income: null,
            state_pension_included: true,
            total_debt_excluding_mortgage: null,
            comfortable_with_debt: true
        });
    };

    const handleGoalsChange = (newGoals) => {
        const yearsToRetirement = newGoals.targetRetirementAge - personalProfile.age;
        setGoals({ ...newGoals, yearsToRetirement });
    };

    const canProceed = () => {
        switch (currentStep) {
            case 1:
                return personalProfile.age > 0;
            case 2:
                return financialProfile.pensionValue > 0 && financialProfile.annualSalary > 0;
            case 3:
                return contributionDetails.employeeContribution >= 0 &&
                    contributionDetails.employerContribution >= 0 &&
                    contributionDetails.monthlyDisposable >= 0;
            case 4:
                return goals.yearsToRetirement > 0 && goals.incomeInRetirement > 0;
            case 5:
                return true;
            case 6:
                const riskComplete = riskQuestions.every(q => riskAnswers[q.id] !== undefined);
                const capacityComplete = capacityQuestions.every(q => capacityAnswers[q.id] !== undefined);
                return riskComplete && capacityComplete && !loading && !questionsLoading;
            default:
                return true;
        }
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 1:
                return { icon: 'user', title: 'Personal Profile', subtitle: 'Tell us about yourself' };
            case 2:
                return { icon: 'wallet', title: 'Financial Situation', subtitle: 'Your current financial position' };
            case 3:
                return { icon: 'money-bill-1', title: 'Pension Contributions', subtitle: 'Your current contribution details' };
            case 4:
                return { icon: 'bullseye', title: 'Goals & Objectives', subtitle: 'What are you saving for?' };
            case 5:
                return { icon: 'coins', title: 'Tax Position', subtitle: 'ISA, pension allowances & carry forward' };
            case 6:
                return { icon: 'balance-scale', title: 'Risk Assessment', subtitle: 'Understanding your risk tolerance and capacity' };
            case 7:
                return { icon: 'check-circle', title: 'Your Recommendations', subtitle: 'AI-powered FCA-compliant pension advice' };
            default:
                return { icon: 'user', title: '', subtitle: '' };
        }
    };

    const stepInfo = getStepTitle();

    return (
        <>
            {showSuitabilityReport ? (
                <SuitabilityReport
                    personalProfile={personalProfile}
                    financialProfile={financialProfile}
                    goals={goals}
                    contributionDetails={contributionDetails}
                    riskAssessment={backendRecommendation?.risk_assessment}
                    fundRecommendations={backendRecommendation?.fund_recommendations}
                    contributionRecommendation={contributionRecommendation}
                    backendRecommendation={backendRecommendation}
                    complianceReport={complianceReport}
                    onBack={() => setShowSuitabilityReport(false)}
                />
            ) : (
                <>
                    <Container className="py-4" style={{ maxWidth: '900px' }}>
                        <StepIndicator steps={steps} currentStep={currentStep} onStepClick={setCurrentStep} recommendationReady={backendRecommendation !== null} />

                        <Card className="shadow-sm mt-4">
                            <Card.Body className="p-4">
                                <div className="mb-4 pb-3 border-bottom">
                                    <h2 className="h4 mb-1">
                                        <FontAwesomeIcon icon={stepInfo.icon} className="me-2 text-primary" />
                                        {stepInfo.title}
                                    </h2>
                                    <p className="text-muted mb-0">{stepInfo.subtitle}</p>
                                </div>

                                {error && (
                                    <div className="alert alert-danger mb-4">
                                        <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                        {error}
                                    </div>
                                )}

                                {/* Step 1: Personal Profile */}
                                {currentStep === 1 && (
                                    <PersonalProfile
                                        profile={personalProfile}
                                        onChange={setPersonalProfile}
                                    />
                                )}

                                {/* Step 2: Financial Situation */}
                                {currentStep === 2 && (
                                    <FinancialSituation
                                        profile={financialProfile}
                                        onChange={setFinancialProfile}
                                    />
                                )}

                                {/* Step 3: Contribution Details */}
                                {currentStep === 3 && (
                                    <ContributionDetails
                                        contribution={contributionDetails}
                                        onChange={setContributionDetails}
                                        salary={financialProfile.annualSalary}
                                        netMonthlyIncome={financialProfile.netMonthlyIncome || 0}
                                    />
                                )}

                                {/* Step 4: Goals & Objectives */}
                                {currentStep === 4 && (
                                    <GoalsObjectives
                                        goals={goals}
                                        onChange={handleGoalsChange}
                                        age={personalProfile.age}
                                    />
                                )}

                                {/* Step 5: Tax Position */}
                                {currentStep === 5 && (
                                    <TaxPosition
                                        profile={{
                                            ...taxPosition,
                                            annual_income: financialProfile.annualSalary
                                        }}
                                        contributionDetails={contributionDetails}
                                        salary={financialProfile.annualSalary}
                                        onChange={handleTaxPositionChange}
                                    />
                                )}

                                {/* Step 6: Risk Assessment */}
                                {currentStep === 6 && (
                                    <>
                                        {questionsLoading ? (
                                            <div className="text-center py-4">
                                                <FontAwesomeIcon icon="spinner" className="fa-spin fa-2x text-primary" />
                                                <p className="mt-2 text-muted">Loading questions...</p>
                                            </div>
                                        ) : questionsError ? (
                                            <div className="alert alert-danger">
                                                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                                Failed to load questions: {questionsError}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mb-4">
                                                    <h5 className="mb-3">
                                                        <FontAwesomeIcon icon="chart-line" className="me-2 text-primary" />
                                                        Risk Tolerance
                                                    </h5>
                                                    <p className="text-muted small mb-3">
                                                        These questions help us understand your attitude to investment risk.
                                                    </p>
                                                    <RiskQuestionnaire
                                                        questions={riskQuestions}
                                                        answers={riskAnswers}
                                                        onAnswer={handleRiskAnswer}
                                                    />
                                                </div>

                                                <hr className="my-4" />

                                                <div>
                                                    <h5 className="mb-3">
                                                        <FontAwesomeIcon icon="shield-alt" className="me-2 text-warning" />
                                                        Capacity for Loss
                                                    </h5>
                                                    <p className="text-muted small mb-3">
                                                        These questions help us understand your ability to absorb investment losses.
                                                    </p>
                                                    <CapacityForLoss
                                                        questions={capacityQuestions}
                                                        answers={capacityAnswers}
                                                        onAnswer={handleCapacityAnswer}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Step 7: Recommendations */}
                                {currentStep === 7 && (
                                    <>
                                        {contributionRecommendation && (
                                            <div className="mb-5">
                                                <h3 className="mb-4">
                                                    <FontAwesomeIcon icon="hand-holding-usd" className="me-2 text-primary" />
                                                    AI-Powered Contribution Advice
                                                </h3>
                                                <ContributionRecommendation
                                                    contributionResult={contributionRecommendation}
                                                    salary={financialProfile.annualSalary}
                                                />
                                            </div>
                                        )}

                                        {/* Pension Projection - shows before/after comparison */}
                                        {backendRecommendation && (
                                            <div className="mt-5">
                                                <PensionProjection
                                                    currentPotValue={financialProfile.pensionValue}
                                                    annualContribution={(contributionDetails.employeeContribution + contributionDetails.employerContribution) / 100 * financialProfile.annualSalary}
                                                    yearsToRetirement={goals.yearsToRetirement}
                                                    currentSalary={financialProfile.annualSalary}
                                                    retirementAge={goals.targetRetirementAge}
                                                    recommendedContribution={contributionRecommendation?.recommended_monthly_contribution ? contributionRecommendation.recommended_monthly_contribution * 12 : null}
                                                />
                                            </div>
                                        )}

                                        <hr className="my-5" />

                                        {backendRecommendation && (
                                            <div>
                                                <h3 className="mb-4">
                                                    <FontAwesomeIcon icon="chart-pie" className="me-2 text-primary" />
                                                    AI Fund Recommendation
                                                </h3>
                                                <RecommendationCard
                                                    recommendation={backendRecommendation}
                                                    profile={{
                                                        personal: personalProfile,
                                                        financial: financialProfile,
                                                        contribution: contributionDetails,
                                                        goals: goals,
                                                        taxPosition: taxPosition
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {complianceReport && (
                                            <div className="mb-5 mt-5">
                                                <ComplianceDashboard recommendation={backendRecommendation} />
                                            </div>
                                        )}
                                        {/* Suitability Report - Button to open full page */}
                                        {backendRecommendation && (
                                            <div className="mt-5">
                                                <hr className="my-4" />
                                                <div className="text-center">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="lg"
                                                        onClick={() => setShowSuitabilityReport(true)}
                                                    >
                                                        <FontAwesomeIcon icon="file-alt" className="me-2" />
                                                        View Suitability Report
                                                    </Button>
                                                    <p className="text-muted small mt-2">
                                                        Generate a printable suitability report
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                    </>
                                )}

                                {/* Navigation Buttons */}
                                <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                                    {currentStep > 1 && currentStep < 7 ? (
                                        <Button variant="outline-secondary" onClick={handleBack} disabled={loading}>
                                            <FontAwesomeIcon icon="arrow-left" className="me-2" />
                                            Back
                                        </Button>
                                    ) : (
                                        <div />
                                    )}

                                    {currentStep < 7 ? (
                                        <Button
                                            variant="primary"
                                            onClick={handleNext}
                                            disabled={!canProceed()}
                                            size="lg"
                                        >
                                            {loading ? (
                                                <>
                                                    <FontAwesomeIcon icon="spinner" className="me-2 fa-spin" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    {currentStep === 6 ? 'Get Recommendations' : 'Continue'}
                                                    <FontAwesomeIcon icon="arrow-right" className="ms-2" />
                                                </>
                                            )}
                                        </Button>
                                    ) : (
                                        <Button variant="outline-primary" onClick={handleReset}>
                                            <FontAwesomeIcon icon="redo" className="me-2" />
                                            Start New Assessment
                                        </Button>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>

                        <div className="text-center mt-4 text-muted">
                            <small>
                                FCA-Compliant AI Driven Recommendations
                            </small>
                        </div>
                    </Container>
                </>
            )}
        </>
    );
}

// =============================================================================
// App with Auth
// =============================================================================

function AppContent() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);

    // Check for existing session on mount
    useEffect(() => {
        const checkSession = async () => {
            if (authService.isLoggedIn()) {
                const result = await authService.validateSession();
                if (result.valid) {
                    setUser(result.user || authService.getUser());
                    setIsAuthenticated(true);
                }
            }
            setCheckingAuth(false);
        };
        checkSession();
    }, []);

    const handleLoginSuccess = (userData, token) => {
        setUser(userData);
        setIsAuthenticated(true);
    };

    const handleLogout = async () => {
        await authService.logout();
        setUser(null);
        setIsAuthenticated(false);
    };

    // Show loading while checking auth
    if (checkingAuth) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="text-center">
                    <FontAwesomeIcon icon="spinner" spin size="3x" className="text-primary mb-3" />
                    <p className="text-muted">Loading...</p>
                </div>
            </div>
        );
    }

    // Show login if not authenticated
    if (!isAuthenticated) {
        return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
    }

    // Authenticated - show app
    return (
        <div className="min-vh-100 bg-light">
            <Header user={user} onLogout={handleLogout} />
            <Routes>
                <Route path="/" element={<AssessmentWizard user={user} />} />
                <Route path="/admin" element={<WaiveAdmin user={user} />} />
                <Route path="/methodology" element={<MethodologyPage />} />
            </Routes>
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AppContent />
        </BrowserRouter>
    );
}

export default App;